import React, { useMemo, useState } from 'react';
import { Button, Card, Input, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import managementReviewService, {
  type ManagementReviewMeeting,
} from '@/services/managementReview.service';
import { useAuthStore } from '@/store/authStore';

const { Title, Text } = Typography;

const statusColor = (status: string) => {
  const map: Record<string, string> = {
    Draft: 'default',
    'Pending Agenda Approval': 'processing',
    'Agenda Approved': 'blue',
    'Minutes In Progress': 'cyan',
    'Pending Minutes Approval': 'orange',
    Closed: 'success',
  };
  return map[status] || 'default';
};

const ManagementReviewsPage: React.FC = () => {
  const navigate = useNavigate();
  const perms = (useAuthStore((s) => s.user?.permissions) ?? []) as string[];
  const canCreate =
    perms.includes('quality.management_review.create') ||
    perms.includes('quality.management_review.edit') ||
    perms.length === 0;

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<string | undefined>();
  const [overdueOnly, setOverdueOnly] = useState(false);

  const { data: rows = [], isLoading, refetch, isFetching } = useQuery({
    queryKey: ['management-reviews', status, overdueOnly],
    queryFn: () =>
      managementReviewService.list({
        status: status || undefined,
        overdue_next: overdueOnly ? '1' : undefined,
      }),
  });

  const { data: stats } = useQuery({
    queryKey: ['management-reviews', 'dashboard-stats'],
    queryFn: () => managementReviewService.dashboardStats(),
  });

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.mrm_number.toLowerCase().includes(q) ||
        String(r.venue || '')
          .toLowerCase()
          .includes(q) ||
        String(r.place_of_meeting || '')
          .toLowerCase()
          .includes(q)
    );
  }, [rows, search]);

  const columns: ColumnsType<ManagementReviewMeeting> = [
    {
      title: 'MRM No',
      dataIndex: 'mrm_number',
      width: 140,
      render: (v, r) => <a onClick={() => navigate(`/qms/management-reviews/${r.id}`)}>{v}</a>,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 180,
      render: (v: string) => <Tag color={statusColor(v)}>{v}</Tag>,
    },
    {
      title: 'Proposed / Meeting',
      key: 'dates',
      width: 180,
      render: (_, r) => (
        <Space direction="vertical" size={0}>
          <Text type="secondary">
            Proposed: {r.proposed_date ? dayjs(r.proposed_date).format('DD/MM/YYYY') : '—'}
          </Text>
          <Text>
            Meeting: {r.meeting_date ? dayjs(r.meeting_date).format('DD/MM/YYYY') : '—'}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Venue',
      key: 'venue',
      ellipsis: true,
      render: (_, r) => r.place_of_meeting || r.venue || '—',
    },
    {
      title: 'Open actions',
      dataIndex: 'open_action_count',
      width: 110,
      align: 'right',
      render: (v) => v ?? 0,
    },
    {
      title: 'Next MRM',
      dataIndex: 'next_mrm_date',
      width: 130,
      render: (v, r) =>
        v ? (
          <Space>
            {dayjs(v).format('DD/MM/YYYY')}
            {Number(r.overdue_next) ? <Tag color="red">Overdue</Tag> : null}
          </Space>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <div>
          <Title level={3} style={{ margin: 0 }}>
            Management Review Meetings
          </Title>
          <Text type="secondary">
            QMS annual management review — agenda, minutes, actions and Top Management approval.
          </Text>
        </div>
        <Space wrap>
          <Button icon={<ReloadOutlined />} loading={isFetching} onClick={() => refetch()}>
            Refresh
          </Button>
          {canCreate ? (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => navigate('/qms/management-reviews/new')}
            >
              New MRM
            </Button>
          ) : null}
        </Space>
      </Space>

      <Space wrap style={{ marginBottom: 16 }}>
        <Card size="small">
          In flight: <Text strong>{stats?.in_flight ?? '—'}</Text>
        </Card>
        <Card size="small">
          Open actions: <Text strong>{stats?.open_actions ?? '—'}</Text>
        </Card>
        <Card size="small">
          Overdue next MRM: <Text strong type={stats?.overdue_next_mrm ? 'danger' : undefined}>
            {stats?.overdue_next_mrm ?? '—'}
          </Text>
        </Card>
      </Space>

      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Input.Search
            allowClear
            placeholder="Search MRM no / venue"
            style={{ width: 260 }}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 220 }}
            value={status}
            onChange={setStatus}
            options={[
              'Draft',
              'Pending Agenda Approval',
              'Agenda Approved',
              'Minutes In Progress',
              'Pending Minutes Approval',
              'Closed',
            ].map((s) => ({ value: s, label: s }))}
          />
          <Select
            style={{ width: 180 }}
            value={overdueOnly ? '1' : '0'}
            onChange={(v) => setOverdueOnly(v === '1')}
            options={[
              { value: '0', label: 'All next dates' },
              { value: '1', label: 'Overdue next MRM' },
            ]}
          />
        </Space>
        <Table
          rowKey="id"
          loading={isLoading}
          columns={columns}
          dataSource={filtered}
          pagination={{ pageSize: 20 }}
          onRow={(r) => ({
            onClick: () => navigate(`/qms/management-reviews/${r.id}`),
            style: { cursor: 'pointer' },
          })}
        />
      </Card>
    </div>
  );
};

export default ManagementReviewsPage;
